import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * A command the current page contributes to the command palette while it is
 * mounted. Pages describe what the command does; the palette owns rendering,
 * ranking, and dismissal — it never knows which surface (core or plugin)
 * registered an entry. The provider lives in `PageCommandsProvider.tsx`,
 * mounted once in `AppShell`.
 */
export interface PageCommand {
  /** Stable id within the registering surface; used as the item key/value. */
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Extra search terms for the fuzzy scorer. */
  keywords?: readonly string[];
  /** Invoked after the palette closes (deferred one tick, like actions). */
  run: () => void;
}

export type PageCommandsContributor = (id: symbol, commands: readonly PageCommand[] | null) => void;

export const PageCommandsStateContext = createContext<readonly PageCommand[]>([]);
export const PageCommandsDispatchContext = createContext<PageCommandsContributor | null>(null);

/** Commands registered by whatever page is currently mounted. */
export function usePageCommands(): readonly PageCommand[] {
  return useContext(PageCommandsStateContext);
}

/**
 * Publish this surface's commands into the palette for as long as the caller
 * stays mounted; unmounting (leaving the page) withdraws them. Pass a
 * memoized array — the registration re-runs on identity change. No-ops
 * outside `PageCommandsProvider` so embedded/preview renders stay inert.
 */
export function useRegisterPageCommands(commands: readonly PageCommand[]): void {
  const contribute = useContext(PageCommandsDispatchContext);
  const idRef = useRef(Symbol('page-commands'));

  useEffect(() => {
    if (!contribute) return;
    const id = idRef.current;
    contribute(id, commands);
    return () => contribute(id, null);
  }, [contribute, commands]);
}
